import { yupResolver } from "@hookform/resolvers/yup";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { DealCardModalForm } from "~/components/common/deals/show/deal_card/DealCardModalForm";
import type { Deal } from "~/models/Deal";
import type { DealUpdateParam } from "~/models/DealForm";
import {
	buildDealUpdateParam,
	normalizeFields,
	updateValidationSchemaV2 as updateValidationSchema
} from "~/models/DealForm";
import { isSuccess } from "~/utils/api/api_handler";
import { update } from "~/utils/api/Deal";
import { onInvalidScroll } from "~/utils/FormUtil";

type Props = {
	deal: Deal;
	handleClose: () => void;
	handleMutate?: () => Promise<void>;
};

export function DealCardModal({ deal, handleClose, handleMutate }: Props) {
	const { notify } = useNotification();
	const methods = useForm<DealUpdateParam>({
		defaultValues: buildDealUpdateParam(deal),
		mode: "onBlur",
		resolver: yupResolver(updateValidationSchema)
	});
	const isSubmitting = methods.formState.isSubmitting;
	const handleModalSubmit = useCallback(() => {
		void methods.handleSubmit(
			async () => {
				const response = await update(
					deal.id,
					normalizeFields(methods.getValues())
				);
				if (isSuccess(response)) {
					await handleMutate?.();
					handleClose();
					notify({ content: "更新しました。", variant: "success" });
					return;
				}
				handleClose();
				notify({ content: "エラーが発生しました。", variant: "error" });
			},
			(errors) => {
				onInvalidScroll(errors);
			}
		)();
	}, [methods, deal.id, handleClose, notify, handleMutate]);
	const handleModalClose = useCallback(() => {
		if (isSubmitting) return;
		handleClose();
	}, [handleClose, isSubmitting]);
	return (
		<Modalv2 isOpen onClose={handleModalClose} size="l">
			<ModalHeader title="案件情報の更新" />
			<ModalBody>
				<FormProvider {...methods}>
					<DealCardModalForm deal={deal} />
				</FormProvider>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Buttonv2
						disabled={isSubmitting}
						onClick={handleModalClose}
						variant="secondary"
					>
						キャンセル
					</Buttonv2>
				}
				rightArea={
					<Buttonv2
						loading={methods.formState.isSubmitting}
						onClick={handleModalSubmit}
						variant="primary"
					>
						更新
					</Buttonv2>
				}
			/>
		</Modalv2>
	);
}
